import type { VariantProps } from "class-variance-authority";
import * as React from "react";
import * as LabelPrimitive from "@radix-ui/react-label";
import { cva } from "class-variance-authority";
import { CircleHelp } from "lucide-react";

import { cn } from "~/lib/utils";
import { Button } from "./button";
import {
  HybridTooltip,
  HybridTooltipContent,
  HybridTooltipTrigger,
} from "./hybrid-tooltip";

export interface LabelProps
  extends React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>,
    VariantProps<typeof labelVariants> {
  helperText?: string;
}

const labelVariants = cva(
  "leading-none peer-disabled:cursor-not-allowed font-medium peer-disabled:opacity-70",
  {
    variants: {
      size: {
        default: "text-sm",
        lg: "text-sm uppercase font-medium",
      },
    },
    defaultVariants: {
      size: "default",
    },
  },
);

const Label = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  LabelProps
>(({ className, size, helperText, ...props }, ref) => (
  <div className="flex flex-row gap-1 items-center">
    <LabelPrimitive.Root
      ref={ref}
      className={cn("w-full", labelVariants({ size }), className)}
      {...props}
    />
    {helperText && (
      <HybridTooltip>
        <HybridTooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            className="p-2 w-8 h-8 rounded-full"
          >
            <CircleHelp />
          </Button>
        </HybridTooltipTrigger>
        <HybridTooltipContent>
          <p>{helperText}</p>
        </HybridTooltipContent>
      </HybridTooltip>
    )}
  </div>
));
Label.displayName = LabelPrimitive.Root.displayName;

export { Label };
